// src/components/course/pages/LessonDetail.jsx
import React, { useState, useEffect } from 'react';
import {
  ArrowLeft, ArrowRight, CheckCircle, Clock, Star, BookOpen, Play,
  FileText, HelpCircle, Image as ImageIcon, Target, MessageSquare,
  Edit3, Eye, Info, TrendingUp, ChevronDown, ChevronUp
} from 'lucide-react';
import { courseService } from '../../../services/courseService';
import InteractiveAssessment from '../interactive/InteractiveAssessment';
import LessonNotes from '../interactive/LessonNotes';
import { UnderstandingRating } from '../interactive/UnderstandingRating';
import { MathRenderer } from '../../math/MathRenderer';

const LessonDetail = ({ bookId, courseId, lessonId, user, userProfile, onNavigate }) => {
  const [lesson, setLesson] = useState(null);
  const [course, setCourse] = useState(null);
  const [allLessons, setAllLessons] = useState([]);
  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentBlockIndex, setCurrentBlockIndex] = useState(0);
  const [completedBlocks, setCompletedBlocks] = useState(new Set());
  const [lessonProgress, setLessonProgress] = useState({});
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [notesExpanded, setNotesExpanded] = useState(false);

  const viewMode = userProfile?.isCreator ? 'creator' : 'learner';

  useEffect(() => {
    if (lessonId && courseId && bookId) {
      loadLessonData();
    }
  }, [lessonId, courseId, bookId, user]);

  const loadLessonData = async () => {
    try {
      setLoading(true);

      // Load lesson, course, and all lessons in parallel
      const [lessonData, courseData, lessonsData] = await Promise.all([
        courseService.getLesson(bookId, courseId, lessonId),
        courseService.getCourse(bookId, courseId),
        courseService.getLessons(bookId, courseId)
      ]);

      setLesson(lessonData);
      setCourse(courseData);
      setAllLessons(lessonsData);

      // Load user enrollment and progress if authenticated
      if (user) {
        const enrollmentData = await courseService.getEnrollment(bookId, courseId, user.uid);
        setEnrollment(enrollmentData);

        // Initialize completed blocks from enrollment data
        if (enrollmentData?.completedLessons?.includes(lessonId)) {
          // Mark all blocks as completed if lesson is completed
          const allBlockIds = new Set(lessonData.contentBlocks?.map((_, idx) => idx) || []);
          setCompletedBlocks(allBlockIds);
        }
      }

    } catch (error) {
      console.error('Error loading lesson:', error);
      alert('Failed to load lesson');
    } finally {
      setLoading(false);
    }
  };

  const handleBlockComplete = async (blockIndex) => {
    setCompletedBlocks(prev => new Set([...prev, blockIndex]));

    // Update progress in database
    if (user && enrollment) {
      try {
        const allCompleted = blockIndex === (lesson.contentBlocks?.length || 0) - 1;
        if (allCompleted) {
          await courseService.updateProgress(bookId, courseId, user.uid, lessonId);
        }
      } catch (error) {
        console.error('Error updating progress:', error);
      }
    }
  };

  const handleAssessmentComplete = async (blockIndex, results) => {
    setLessonProgress(prev => ({
      ...prev,
      [`assessment_${blockIndex}`]: results
    }));
    handleBlockComplete(blockIndex);
  };

  const handleUnderstandingRating = async (blockIndex, rating) => {
    setLessonProgress(prev => ({
      ...prev,
      [`understanding_${blockIndex}`]: rating
    }));
  };

  const handleNotesUpdate = (notes) => {
    setLessonProgress(prev => ({
      ...prev,
      notes
    }));
  };

  const navigateToLesson = (targetLessonId) => {
    onNavigate('lesson', { courseId, lessonId: targetLessonId });
  };

  const getCurrentLessonIndex = () => {
    return allLessons.findIndex(l => l.id === lessonId);
  };

  const getPreviousLesson = () => {
    const currentIndex = getCurrentLessonIndex();
    return currentIndex > 0 ? allLessons[currentIndex - 1] : null;
  };

  const getNextLesson = () => {
    const currentIndex = getCurrentLessonIndex();
    return currentIndex < allLessons.length - 1 ? allLessons[currentIndex + 1] : null;
  };

  const isLessonCompleted = () => {
    return completedBlocks.size === (lesson?.contentBlocks?.length || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading lesson...</p>
        </div>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Lesson not found</h2>
          <button
            onClick={() => onNavigate('course', { courseId })}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Back to course
          </button>
        </div>
      </div>
    );
  }

  const currentBlock = lesson.contentBlocks?.[currentBlockIndex];
  const totalBlocks = lesson.contentBlocks?.length || 0;
  const previousLesson = getPreviousLesson();
  const nextLesson = getNextLesson();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => onNavigate('course', { courseId })}
                className="text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-gray-900 truncate">{lesson.title}</h1>
                <p className="text-sm text-gray-600 truncate">{course?.title}</p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {viewMode === 'creator' && (
                <button
                  onClick={() => onNavigate('lesson-edit', { courseId, lessonId })}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  <Edit3 className="w-4 h-4" />
                  <span className="hidden sm:inline">Edit</span>
                </button>
              )}
              
              {isLessonCompleted() && (
                <div className="flex items-center space-x-2 text-green-600">
                  <CheckCircle className="w-5 h-5" />
                  <span className="hidden sm:inline text-sm font-medium">Completed</span>
                </div>
              )}
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
              <span>Lesson Progress</span>
              <span>{completedBlocks.size} / {totalBlocks} blocks</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${totalBlocks > 0 ? (completedBlocks.size / totalBlocks) * 100 : 0}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Content Block */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {/* Block Navigation */}
              {totalBlocks > 1 && (
                <div className="bg-gray-50 border-b border-gray-200 p-4">
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => setCurrentBlockIndex(Math.max(0, currentBlockIndex - 1))}
                      disabled={currentBlockIndex === 0}
                      className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span className="text-sm">Previous</span>
                    </button>

                    <div className="flex items-center space-x-2">
                      {lesson.contentBlocks?.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentBlockIndex(index)}
                          className={`w-2 h-2 rounded-full transition-colors ${
                            index === currentBlockIndex
                              ? 'bg-blue-600 w-4'
                              : completedBlocks.has(index)
                              ? 'bg-green-500'
                              : 'bg-gray-300'
                          }`}
                          title={`Block ${index + 1}`}
                        />
                      ))}
                    </div>

                    <button
                      onClick={() => setCurrentBlockIndex(Math.min(totalBlocks - 1, currentBlockIndex + 1))}
                      disabled={currentBlockIndex === totalBlocks - 1}
                      className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="text-sm">Next</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Block Content */}
              <div className="p-6">
                {currentBlock ? (
                  <LessonContent
                    block={currentBlock}
                    blockIndex={currentBlockIndex}
                    totalBlocks={totalBlocks}
                    viewMode={viewMode}
                    isCompleted={completedBlocks.has(currentBlockIndex)}
                    onComplete={() => handleBlockComplete(currentBlockIndex)}
                    onAssessmentComplete={(results) => handleAssessmentComplete(currentBlockIndex, results)}
                    onUnderstandingRating={(rating) => handleUnderstandingRating(currentBlockIndex, rating)}
                    lessonProgress={{ ...lessonProgress, totalBlocks }}
                  />
                ) : (
                  <div className="text-center py-12">
                    <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600">No content available</p>
                  </div>
                )}
              </div>
            </div>

            {/* Lesson Notes - Only show after last block */}
            {viewMode === 'learner' && currentBlockIndex === totalBlocks - 1 && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <button
                  onClick={() => setNotesExpanded(!notesExpanded)}
                  className="w-full p-4 flex items-center justify-between hover:bg-gray-50"
                >
                  <div className="flex items-center space-x-3">
                    <MessageSquare className="w-5 h-5 text-gray-600" />
                    <span className="font-medium text-gray-900">Lesson Notes</span>
                  </div>
                  {notesExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                
                {notesExpanded && (
                  <div className="p-4 border-t border-gray-200">
                    <LessonNotes
                      lessonId={lessonId}
                      userId={user?.uid}
                      initialNotes={lessonProgress.notes || ''}
                      onUpdate={handleNotesUpdate}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Lesson Navigation */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="grid grid-cols-2 gap-4">
                {previousLesson ? (
                  <button
                    onClick={() => navigateToLesson(previousLesson.id)}
                    className="flex items-center space-x-3 text-left hover:bg-gray-50 p-3 rounded-lg transition-colors group"
                  >
                    <ArrowLeft className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
                    <div>
                      <p className="text-sm text-gray-500">Previous</p>
                      <p className="font-medium text-gray-900 truncate">{previousLesson.title}</p>
                    </div>
                  </button>
                ) : (
                  <div></div>
                )}

                {nextLesson ? (
                  <button
                    onClick={() => navigateToLesson(nextLesson.id)}
                    className="flex items-center justify-end space-x-3 text-right hover:bg-gray-50 p-3 rounded-lg transition-colors group"
                  >
                    <div>
                      <p className="text-sm text-gray-500">Next</p>
                      <p className="font-medium text-gray-900 truncate">{nextLesson.title}</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
                  </button>
                ) : isLessonCompleted() ? (
                  <button
                    onClick={() => onNavigate('course', { courseId })}
                    className="flex items-center justify-end space-x-3 text-right bg-green-50 hover:bg-green-100 p-3 rounded-lg transition-colors group"
                  >
                    <div>
                      <p className="text-sm text-green-600">Completed!</p>
                      <p className="font-medium text-green-700">Back to Course</p>
                    </div>
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </button>
                ) : (
                  <div></div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Sidebar */}
          {!sidebarCollapsed && (
            <div className="lg:col-span-1 space-y-6">
              {/* Lesson Info */}
              <LessonInfo lesson={lesson} course={course} />

              {/* Course Progress */}
              {viewMode === 'learner' && allLessons.length > 0 && (
                <CourseProgress
                  allLessons={allLessons}
                  currentLessonId={lessonId}
                  courseId={courseId}
                  enrollment={enrollment}
                  onNavigate={onNavigate}
                />
              )}

              {/* Creator Insights */}
              {viewMode === 'creator' && (
                <CreatorInsights
                  lesson={lesson}
                  lessonProgress={lessonProgress}
                  completedBlocks={completedBlocks}
                  courseId={courseId}
                  lessonId={lessonId}
                  onNavigate={onNavigate}
                />
              )}

              {/* Learning Features */}
              {viewMode === 'learner' && <LearningFeatures />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Lesson Content Renderer
const LessonContent = ({
  block,
  blockIndex,
  totalBlocks,
  viewMode,
  isCompleted,
  onComplete,
  onAssessmentComplete,
  onUnderstandingRating,
  lessonProgress
}) => {
  const getBlockIcon = (type) => {
    switch (type) {
      case 'video': return Play;
      case 'text': return FileText;
      case 'image': return ImageIcon;
      case 'quiz': return HelpCircle;
      case 'assessment': return Target;
      default: return BookOpen;
    }
  };

  const BlockIcon = getBlockIcon(block.type);

  return (
    <div className="space-y-6">
      {/* Block Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            isCompleted ? 'bg-green-100' : 'bg-blue-100'
          }`}>
            <BlockIcon className={`w-5 h-5 ${isCompleted ? 'text-green-600' : 'text-blue-600'}`} />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">{block.title}</h3>
            <p className="text-sm text-gray-500 capitalize">{block.type}</p>
          </div>
        </div>

        {isCompleted && (
          <div className="flex items-center space-x-2 text-green-600">
            <CheckCircle className="w-5 h-5" />
            <span className="text-sm font-medium">Completed</span>
          </div>
        )}
      </div>

      {/* Instructions */}
      {block.instructions && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">{block.instructions}</p>
        </div>
      )}

      {/* Block Content */}
      <div className="space-y-4">
        {block.type === 'video' && <VideoBlock block={block} onComplete={onComplete} />}
        {block.type === 'text' && <TextBlock block={block} onComplete={onComplete} />}
        {block.type === 'image' && <ImageBlock block={block} onComplete={onComplete} />}
        {block.type === 'quiz' && <QuizBlock block={block} onComplete={onComplete} />}
        {block.type === 'assessment' && (
          <InteractiveAssessment
            block={block}
            onComplete={onAssessmentComplete}
          />
        )}
      </div>

      {/* Understanding Rating */}
      {viewMode === 'learner' && blockIndex === (lessonProgress.totalBlocks - 1) && (
        <UnderstandingRating
          blockIndex={blockIndex}
          currentRating={lessonProgress[`understanding_${blockIndex}`]}
          onRate={onUnderstandingRating}
        />
      )}
    </div>
  );
};

// Video Block
const VideoBlock = ({ block, onComplete }) => {
  const [videoCompleted, setVideoCompleted] = useState(false);

  const getVideoEmbedUrl = (url) => {
    if (!url) return null;

    // YouTube
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    }

    // Vimeo
    if (url.includes('vimeo.com')) {
      const videoId = url.match(/vimeo\.com\/(\d+)/)?.[1];
      return videoId ? `https://player.vimeo.com/video/${videoId}` : null;
    }

    return url;
  };

  const videoUrl = block.videoFileUrl || getVideoEmbedUrl(block.videoUrl);

  return (
    <div className="space-y-4">
      {videoUrl ? (
        <div className="relative rounded-lg overflow-hidden bg-black" style={{ paddingTop: '56.25%' }}>
          {block.videoFileUrl ? (
            <video
              src={videoUrl}
              controls
              className="absolute top-0 left-0 w-full h-full"
              onEnded={() => {
                setVideoCompleted(true);
                onComplete();
              }}
            />
          ) : (
            <iframe
              src={videoUrl}
              className="absolute top-0 left-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          )}
        </div>
      ) : (
        <div className="bg-gray-100 rounded-lg p-12 text-center">
          <Play className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No video available</p>
        </div>
      )}

      {!videoCompleted && (
        <button
          onClick={() => {
            setVideoCompleted(true);
            onComplete();
          }}
          className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Mark as Completed
        </button>
      )}
    </div>
  );
};

// Text Block
const TextBlock = ({ block, onComplete }) => {
  const [read, setRead] = useState(false);

  return (
    <div className="space-y-4">
      <div className="prose prose-blue max-w-none">
        <MathRenderer 
          content={block.content || ''}
          enableMarkdown={true}
          enableMath={true}
          className="text-gray-700 leading-relaxed"
        />
      </div>

      {!read && (
        <button
          onClick={() => {
            setRead(true);
            onComplete();
          }}
          className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Mark as Read
        </button>
      )}
    </div>
  );
};

// Image Block
const ImageBlock = ({ block, onComplete }) => {
  const [viewed, setViewed] = useState(false);
  const imageUrl = block.imageFileUrl || block.imageUrl;

  return (
    <div className="space-y-4">
      {imageUrl ? (
        <>
          <img
            src={imageUrl}
            alt={block.title}
            className="w-full rounded-lg border border-gray-200"
            onLoad={() => {
              if (!viewed) {
                setViewed(true);
                onComplete();
              }
            }}
          />
          {block.content && (
            <p className="text-sm text-gray-600 italic">{block.content}</p>
          )}
        </>
      ) : (
        <div className="bg-gray-100 rounded-lg p-12 text-center">
          <ImageIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No image available</p>
        </div>
      )}
    </div>
  );
};

// Quiz Block
const QuizBlock = ({ block, onComplete }) => {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [results, setResults] = useState(null);

  const handleSubmit = () => {
    const score = (block.quizQuestions || []).reduce((acc, question, index) => {
      return acc + (answers[index] === question.correctAnswer ? 1 : 0);
    }, 0);

    const total = block.quizQuestions?.length || 0;
    setResults({ score, total, percentage: Math.round((score / total) * 100) });
    setSubmitted(true);
    onComplete();
  };

  if (!block.quizQuestions || block.quizQuestions.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <HelpCircle className="w-12 h-12 mx-auto mb-2 text-gray-400" />
        <p>No quiz questions available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {block.quizQuestions.map((question, questionIndex) => (
        <div key={questionIndex} className="space-y-3">
          <p className="font-medium text-gray-900">
            {questionIndex + 1}. {question.question}
          </p>

          <div className="space-y-2">
            {question.options.map((option, optionIndex) => {
              const isSelected = answers[questionIndex] === optionIndex;
              const isCorrect = question.correctAnswer === optionIndex;
              const showFeedback = submitted;

              return (
                <button
                  key={optionIndex}
                  onClick={() => !submitted && setAnswers({ ...answers, [questionIndex]: optionIndex })}
                  disabled={submitted}
                  className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${
                    showFeedback && isCorrect
                      ? 'border-green-500 bg-green-50'
                      : showFeedback && isSelected && !isCorrect
                      ? 'border-red-500 bg-red-50'
                      : isSelected
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{option}</span>
                    {showFeedback && isCorrect && (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {submitted && question.explanation && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <strong>Explanation:</strong> {question.explanation}
              </p>
            </div>
          )}
        </div>
      ))}

      {!submitted ? (
        <button
          onClick={handleSubmit}
          disabled={Object.keys(answers).length !== block.quizQuestions.length}
          className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Submit Quiz
        </button>
      ) : (
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 text-center">
          <div className="text-4xl font-bold text-blue-600 mb-2">{results.percentage}%</div>
          <p className="text-gray-700">
            You scored {results.score} out of {results.total}
          </p>
        </div>
      )}
    </div>
  );
};

// Lesson Info Component
const LessonInfo = ({ lesson, course }) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h3 className="font-semibold text-gray-900 mb-4">Lesson Details</h3>

      <div className="space-y-3 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-gray-600">Duration</span>
          <span className="font-medium text-gray-900">
            {Math.floor((lesson.duration || 0) / 60)}h {(lesson.duration || 0) % 60}m
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-gray-600">Content Blocks</span>
          <span className="font-medium text-gray-900">
            {lesson.contentBlocks?.length || 0}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-gray-600">Course</span>
          <span className="font-medium text-gray-900 truncate ml-2">
            {course?.title}
          </span>
        </div>

        {lesson.isPreview && (
          <div className="pt-3 border-t border-gray-200">
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-medium">
              Preview Available
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// Course Progress Component
const CourseProgress = ({ allLessons, currentLessonId, courseId, enrollment, onNavigate }) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h3 className="font-semibold text-gray-900 mb-4">Course Lessons</h3>

      <div className="space-y-2 max-h-96 overflow-y-auto">
        {allLessons.map((lesson, index) => {
          const isCompleted = enrollment?.completedLessons?.includes(lesson.id);
          const isCurrent = lesson.id === currentLessonId;

          return (
            <button
              key={lesson.id}
              onClick={() => onNavigate('lesson', { courseId, lessonId: lesson.id })}
              className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                isCurrent
                  ? 'bg-blue-100 border-2 border-blue-300'
                  : 'hover:bg-gray-50 border-2 border-transparent'
              }`}
            >
              <span className={`text-xs font-medium px-2 py-1 rounded ${
                isCurrent
                  ? 'bg-blue-200 text-blue-800'
                  : isCompleted
                  ? 'bg-green-200 text-green-800'
                  : 'bg-gray-200 text-gray-700'
              }`}>
                {index + 1}
              </span>
              <div className="flex-1 min-w-0 text-left">
                <p className="font-medium text-gray-900 truncate">{lesson.title}</p>
                <p className="text-xs text-gray-500">
                  {Math.floor((lesson.duration || 0) / 60)}h {(lesson.duration || 0) % 60}m
                </p>
              </div>
              {isCompleted && <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />}
              {isCurrent && <Play className="w-4 h-4 text-blue-600 flex-shrink-0" />}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Creator Insights Component
const CreatorInsights = ({ lesson, lessonProgress, completedBlocks, courseId, lessonId, onNavigate }) => {
  const getInsights = () => {
    const insights = [];

    if (completedBlocks.size === 0) {
      insights.push({
        icon: Info,
        text: "No blocks completed yet",
        color: "text-blue-600"
      });
    } else if (completedBlocks.size === lesson.contentBlocks?.length) {
      insights.push({
        icon: CheckCircle,
        text: "All blocks completed",
        color: "text-green-600"
      });
    } else {
      insights.push({
        icon: TrendingUp,
        text: `${completedBlocks.size} of ${lesson.contentBlocks?.length} blocks completed`,
        color: "text-orange-600"
      });
    }

    const assessmentCount = Object.keys(lessonProgress).filter(k => k.startsWith('assessment_')).length;
    if (assessmentCount > 0) {
      insights.push({
        icon: Target,
        text: `${assessmentCount} assessments completed`,
        color: "text-purple-600"
      });
    }

    const ratingCount = Object.keys(lessonProgress).filter(k => k.startsWith('understanding_')).length;
    if (ratingCount > 0) {
      insights.push({
        icon: Star,
        text: `${ratingCount} understanding ratings given`,
        color: "text-yellow-600"
      });
    }

    return insights;
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h3 className="font-semibold text-gray-900 mb-4">Creator Insights</h3>

      <div className="space-y-3">
        {getInsights().map((insight, index) => (
          <div key={index} className="flex items-center space-x-3">
            <insight.icon className={`w-4 h-4 ${insight.color}`} />
            <span className="text-sm text-gray-700">{insight.text}</span>
          </div>
        ))}
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200">
        <button
          onClick={() => onNavigate('lesson-edit', { courseId, lessonId })}
          className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center space-x-1"
        >
          <Edit3 className="w-4 h-4" />
          <span>Edit this lesson</span>
        </button>
      </div>
    </div>
  );
};

// Learning Features Component
const LearningFeatures = () => {
  return (
    <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border border-purple-200 p-6">
      <h3 className="font-semibold text-gray-900 mb-4">Learning Features</h3>

      <div className="space-y-3 text-sm">
        <div className="flex items-start space-x-3">
          <Star className="w-4 h-4 text-purple-600 mt-0.5" />
          <span className="text-gray-700">Rate your understanding after each block</span>
        </div>
        <div className="flex items-start space-x-3">
          <MessageSquare className="w-4 h-4 text-purple-600 mt-0.5" />
          <span className="text-gray-700">Take notes and ask questions</span>
        </div>
        <div className="flex items-start space-x-3">
          <Target className="w-4 h-4 text-purple-600 mt-0.5" />
          <span className="text-gray-700">Complete interactive assessments</span>
        </div>
        <div className="flex items-start space-x-3">
          <CheckCircle className="w-4 h-4 text-purple-600 mt-0.5" />
          <span className="text-gray-700">Track your progress automatically</span>
        </div>
      </div>
    </div>
  );
};

export default LessonDetail;